import { apiInstance } from '@/api/api.js'
import axios from 'axios'
import Vue from 'vue'

const api = apiInstance()
const CORRECT_STATUS = 200

export default {
  async getDirectory(
    { state, commit, dispatch },
    {
      id,
      mainFolderId = false,
      loader = true,
      retry = 1,
      maxRetry = 3,
      pageSize = 50,
      pageNumber = 0,
    },
  ) {
    const queryParams = new URLSearchParams()
    queryParams.append('pageSize', pageSize)
    queryParams.append('pageNumber', pageNumber)
    const url = `/ml/directories/${id}/items?${queryParams.toString()}`

    try {
      if (loader) commit('setLoading', true)
      const response = await api.get(url)
      commit('setDirectory', response.data)
      commit(
        'profilePreviewsStates/setProfilePreviews',
        response.data.profilePreviews,
        {
          root: true,
        },
      )
      let processingEntries = response.data.entries.filter(item => {
        return item.processing == true
      })
      clearTimeout(state.reloadProcessingTimeout)
      clearTimeout(state.reloadTimeout)
      if (processingEntries.length > 0 && retry) {
        let retNext = false
        let timeouts = [5, 25, 60]
        state.reloadProcessingTimeout = setTimeout(() => {
          if (retry < maxRetry) retNext = retry + 1
          dispatch('getDirectory', {
            id: id,
            loader: false,
            retry: retNext,
            pageSize,
            pageNumber,
          })
        }, timeouts[retry - 1] * 1000)
      }
      state.reloadTimeout = setTimeout(() => {
        dispatch('getDirectory', {
          id: id,
          loader: false,
          pageSize,
          pageNumber,
        })
      }, 60000 * 4)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('setLoading', false)
    } catch (error) {
      // redirect to mainFolder if caught 404 on id, but only when id!=mainFolderId
      if (mainFolderId && error.code == 404 && id != mainFolderId && retry) {
        dispatch('getDirectory', {
          id: mainFolderId,
          retry: false,
          pageSize,
          pageNumber,
        })
        state.libraryStates.savedDirectory = false
      }
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getParticipantsDirectory(
    { state, commit, dispatch },
    { id, pageSize = 50, pageNumber = 0 },
  ) {
    const queryParams = new URLSearchParams()
    queryParams.append('pageSize', pageSize)
    queryParams.append('pageNumber', pageNumber)
    const url = `/ml/participants/${id}/resources?${queryParams.toString()}`

    try {
      commit('setLoading', true)
      clearTimeout(state.reloadProcessingTimeout)
      clearTimeout(state.reloadTimeout)
      const response = await api.get(url)
      commit('setDirectory', response.data)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit(
        'profilePreviewsStates/setProfilePreviews',
        response.data.profilePreviews,
        {
          root: true,
        },
      )
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getModalDirectory({ state, commit, dispatch }, { id }) {
    try {
      commit('setLoading', true)
      clearTimeout(state.reloadProcessingTimeout)
      clearTimeout(state.reloadTimeout)
      const response = await api.get(
        `/ml/directories/${id}/items?type=FOLDER&pageSize=500`,
      )
      commit('setModalDirectory', response.data)
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async addDirectory({ state, commit, dispatch }, { id, name }) {
    try {
      commit('setLoading', true)
      await api.post(`/ml/directories/${id}`, { name })
      await dispatch('getDirectory', {
        id: id,
        pageSize: state?.directory?.pageMetadata?.pageSize,
        pageNumber: state?.directory?.pageMetadata?.pageNumber,
      })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'New directory added!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async deleteDirectory(
    { state, commit, dispatch },
    { id, version, parentId, force = false, callback = false },
  ) {
    try {
      commit('setLoading', true)
      await api.delete(`/ml/directories/${id}`, {
        headers: {
          'If-Match': 'W/"' + version + '"',
          'Content-Type': 'application/json',
        },
        data: {
          force: force,
        },
      })
      await dispatch('getDirectory', {
        id: parentId,
        pageSize: state?.directory?.pageMetadata?.pageSize,
        pageNumber: state?.directory?.pageMetadata?.pageNumber,
      })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Directory deleted!', {
        root: true,
      })
    } catch (error) {
      const response = constructDeletionMessage(error)

      if (!response.explanations || response.explanations == '') {
        dispatch('appStates/setErrorMessage', error, { root: true })
      } else {
        if (callback) callback(response)
      }
      commit('setLoading', false)
    }
  },
  async changeDirectory(
    { state, rootState, commit, dispatch },
    { directoryId, parentId, version, key, value, message },
  ) {
    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      await api.put(
        `/ml/directories/${directoryId}`,
        { [key]: value },
        { headers: { 'If-Match': 'W/"' + version + '"' } },
      )
      await dispatch('getDirectory', {
        id: parentId,
        pageSize: state?.directory?.pageMetadata?.pageSize,
        pageNumber: state?.directory?.pageMetadata?.pageNumber,
      })
      commit('setLoading', false)
      if (!message) message = 'Folder edited!'
      commit('appStates/setSnackbarNotification', message, { root: true })
    } catch (error) {
      dispatch(
        'appStates/setErrorMessage',
        { ...error, withoutRetry: true },
        { root: true },
      )
      commit('setLoading', false)
    }
  },
  async getFile({ commit, dispatch }, { directoryId, itemId }) {
    try {
      commit('setLoading', true)
      const response = await api.get(
        `/ml/directories/${directoryId}/items/${itemId}`,
      )
      commit('setItem', response.data)
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async addFile({ state, commit, dispatch }, { directoryId, data }) {
    try {
      commit('setLoading', true)
      await api.post(`/ml/directories/${directoryId}/items`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      await dispatch('getDirectory', {
        id: directoryId,
        pageSize: state?.directory?.pageMetadata?.pageSize,
        pageNumber: state?.directory?.pageMetadata?.pageNumber,
      })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'New file added!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async addFilePromise({ commit, dispatch }, { directoryId, data }) {
    let storageFileId
    try {
      const fileResponse = await dispatch('uploadFile', {
        file: data.file,
        ignoreErrorMessage: true,
      })
      storageFileId = fileResponse.data.id
    } catch (error) {
      console.error(error)
      throw error
    }

    const newData = {
      ...data,
      storageFileId,
    }
    delete newData.file

    return api.post(`/ml/directories/${directoryId}/items`, newData)
  },
  async deleteFile(
    { state, rootState, commit, dispatch },
    { directoryId, itemId, version, force = false, callback = false },
  ) {
    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      await api.delete(`/ml/directories/${directoryId}/items/${itemId}`, {
        headers: { 'If-Match': 'W/"' + version + '"' },
        data: {
          force: force,
        },
      })
      await dispatch('getDirectory', {
        id: directoryId,
        pageSize: state?.directory?.pageMetadata?.pageSize,
        pageNumber: state?.directory?.pageMetadata?.pageNumber,
      })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'File deleted!', {
        root: true,
      })
    } catch (error) {
      const response = constructDeletionMessage(error)

      if (!response.explanations || response.explanations == '') {
        dispatch('appStates/setErrorMessage', error, { root: true })
      } else {
        if (callback) callback(response)
      }
      commit('setLoading', false)
    }
  },
  async changeFile(
    { state, rootState, commit, dispatch },
    { directoryId, itemId, title, version, key, value, message },
  ) {
    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      let data = { [key]: value }
      if (title != value) {
        data.title = title
      }
      await api.put(`/ml/directories/${directoryId}/items/${itemId}`, data, {
        headers: { 'If-Match': 'W/"' + version + '"' },
      })
      await dispatch('getDirectory', {
        id: directoryId,
        pageSize: state?.directory?.pageMetadata?.pageSize,
        pageNumber: state?.directory?.pageMetadata?.pageNumber,
      })
      commit('setLoading', false)
      if (!message) message = 'File edited!'
      commit('appStates/setSnackbarNotification', message, { root: true })
    } catch (error) {
      dispatch(
        'appStates/setErrorMessage',
        { ...error, withoutRetry: true },
        { root: true },
      )
      await dispatch('getDirectory', {
        id: directoryId,
        pageSize: state?.directory?.pageMetadata?.pageSize,
        pageNumber: state?.directory?.pageMetadata?.pageNumber,
      })
      commit('setLoading', false)
    }
  },
  async tagFile(
    { commit, dispatch },
    { directoryId, itemId, version, labels, value },
  ) {
    try {
      await api.put(
        `/ml/directories/${directoryId}/items/${itemId}/labels`,
        { labels: labels },
        { headers: { 'If-Match': 'W/"' + version + '"' } },
      )
    } catch (error) {
      dispatch(
        'appStates/setErrorMessage',
        { ...error, withoutRetry: true },
        { root: true },
      )
    }
  },

  async search(
    { state, rootState, commit, dispatch },
    { directoryId, params },
  ) {
    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      clearTimeout(state.reloadProcessingTimeout)
      clearTimeout(state.reloadTimeout)
      const response = await api.get(
        `/ml/directories/${directoryId}/items/search`,
        { params },
      )
      commit('setDirectory', { entries: response.data })
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async checkFileAvailable(
    { rootState, commit, dispatch },
    { url, directoryId, itemId, name, cb },
  ) {
    try {
      const { headersStates } = rootState
      axios
        .get(url, {
          responseType: 'arraybuffer',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          },
        })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', name) //or any other extension
          document.body.appendChild(link)
          link.click()
        })
        .catch(error => {
          dispatch('getFile', { directoryId: directoryId, itemId: itemId })
          if (cb) {
            cb(itemId)
          } else {
            dispatch('appStates/setErrorMessage', error, { root: true })
          }
        })
    } catch (error) {
      await dispatch('getFile', { directoryId: directoryId, itemId: itemId })
      if (cb) {
        cb(itemId)
      } else {
        dispatch('appStates/setErrorMessage', error, { root: true })
      }
    }
  },
  async scheduleMessagesFromLibrary({ commit, dispatch }, data) {
    try {
      commit('setLoading', true)
      await api.post(`/messages/definitions`, { ...data })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'New messages scheduled!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },

  async uploadFile(
    { commit, dispatch },
    { directoryId, file, ignoreErrorMessage = false },
  ) {
    if (!file) {
      throw new Error('No file provided')
    }

    try {
      const formdata = new FormData()
      formdata.append('file', file)
      const response = await api.post(`/ml/files/upload`, formdata, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response
    } catch (error) {
      if (!ignoreErrorMessage) {
        dispatch('appStates/setErrorMessage', error, { root: true })
      }
      throw error
    }
  },
  async deleteFilesFromDirectory(
    { rootState, commit, dispatch },
    { directoryId, callback = false },
  ) {
    try {
      commit('setLoading', true)
      await api.delete(`/ml/directories/${directoryId}/items`)
      const response = await api.get(`/ml/directories/${directoryId}/items`)
      commit('setDirectory', response.data)
      commit('setLoading', false)
      commit(
        'appStates/setSnackbarNotification',
        'Files deleted from directory!',
        {
          root: true,
        },
      )
    } catch (error) {
      const response = constructDeletionMessage(error)
      if (!response.explanations || response.explanations === '') {
        dispatch('appStates/setErrorMessage', error, { root: true })
      } else {
        if (callback) callback(response)
      }
      commit('setLoading', false)
    }
  },
}

function constructDeletionMessage(error) {
  let response = {
    allowed: true,
    explanations: '',
  }
  const utilTypeDict = {
    MESSAGE_SENT: '0',
    MESSAGE_DEFINITION_IN_THE_PAST: '0',
    MESSAGE_TO_BE_SENT: '1',
    TEMPLATE: '2',
  }
  const utilTypeHeaders = {
    '0':
      'Resources linked to past Participant Sprint messages - will be deleted:',
    '1':
      'Resources linked to scheduled Participant Sprint messages - will be deleted:',
    '2': 'Resources linked to Sprints - can’t be deleted:',
  }
  let utilGroups = []

  if (error.filelist && error.filelist.length > 0) {
    error.filelist.forEach(file => {
      if (file.utilizations && file.utilizations.length > 0) {
        if (
          response.allowed &&
          file.utilizations.find(
            utilization => utilization.eligibleForRemoval == false,
          )
        )
          response.allowed = false
        file.utilizations.forEach(utilization => {
          if (!utilGroups[utilTypeDict[utilization.utilizationType]])
            utilGroups[utilTypeDict[utilization.utilizationType]] = []
          utilGroups[utilTypeDict[utilization.utilizationType]].push(
            utilization.metadata,
          )
        })
      }
    })
  }
  utilGroups.forEach((group, index) => {
    response.explanations +=
      '<strong>' + utilTypeHeaders[index] + '</strong><br/>'
    group.forEach(item => {
      let datetime = ''
      switch (index) {
        case 0: // Resource name: xxx, Participant: xxx, Message: xxx, Scheduled date: xxx
          datetime = item.LOCAL_SEND_DATETIME
            ? item.LOCAL_SEND_DATETIME
            : item.LOCAL_PLANNED_SEND_DATETIME
            ? item.LOCAL_PLANNED_SEND_DATETIME
            : item.LOCAL_FROM == item.LOCAL_TO
            ? item.LOCAL_FROM + ' ' + item.LOCAL_TIME
            : -1
          if (datetime == -1)
            datetime =
              Vue.moment(item.LOCAL_FROM).format('MM/DD/YYYY') +
              ' - ' +
              Vue.moment(item.LOCAL_TO).format('MM/DD/YYYY') +
              ' ' +
              Vue.moment(item.LOCAL_TIME).format('h:mm:ss a')
          else datetime = Vue.moment(datetime).format('MM/DD/YYYY, h:mm:ss a')
          response.explanations +=
            'Resource name: ' +
            item.RESOURCE_NAME +
            ', Participant: ' +
            item.RECIPIENT_FIRST_NAME +
            ' ' +
            item.RECIPIENT_LAST_NAME +
            ', Message: ' +
            item.MESSAGE +
            ', Scheduled date: ' +
            datetime +
            '<br/>'
          break
        case 1: // Resource name: xxx, Participant: xxx, Message: xxxx, Scheduled for sending: xxx
          datetime =
            item.LOCAL_FROM == item.LOCAL_TO
              ? item.LOCAL_FROM + ' ' + item.LOCAL_TIME
              : -1
          if (datetime == -1)
            datetime =
              Vue.moment(item.LOCAL_FROM).format('MM/DD/YYYY') +
              ' - ' +
              Vue.moment(item.LOCAL_TO).format('MM/DD/YYYY') +
              ' ' +
              Vue.moment(item.LOCAL_TIME).format('h:mm:ss a')
          else datetime = Vue.moment(datetime).format('MM/DD/YYYY, h:mm:ss a')

          response.explanations +=
            'Resource name: ' +
            item.RESOURCE_NAME +
            ', Participant: ' +
            item.RECIPIENT_FIRST_NAME +
            ' ' +
            item.RECIPIENT_LAST_NAME +
            ', Message: ' +
            item.MESSAGE +
            ', Scheduled sending for: ' +
            datetime +
            '<br/>'
          break
        case 2: // Resource name: xxx, Sprint: xxx
          response.explanations +=
            'Resource name: ' +
            item.RESOURCE_NAME +
            ', Sprint: ' +
            item.TEMPLATE_NAME +
            '<br/>'
          break
        default:
          break
      }
    })
  })
  return response
}
